import {inject, Injectable} from '@angular/core';
import {RequestDto} from '@dto/requests/request.dto';
import {RequestViewModel} from '../view-models/request.view-model';
import {RequestListDto} from '@dto/requests/request-list.dto';
import {RequestStatusEnum} from '@dto/requests/request-status.enum';
import {Validator} from '@shared/utilities/validator';
import {APP_REQUEST, APP_REQUESTS_COMPLETED, APP_REQUESTS_IN_PROGRESS, APP_REQUESTS_IN_REVIEW} from '../../config/app-urls';
import {RequestDetailDto} from '@dto/requests/request-detail.dto';
import {RequestDetailViewModel} from '../view-models/request-detail.view-model';
import {RequestStepDetailViewModel} from '../view-models/request-step-detail.view-model';
import {RequestStepDetailDto} from '@dto/requests/request-step-detail.dto';
import {RequestStepTypeEnum} from '@dto/requests/request-step-type.enum';
import {RequestStepAdaptiveCardDto} from '@dto/requests/request-step-adaptive-card.dto';
import {RequestStepAdaptiveCardViewModel} from '../view-models/request-step-adaptive-card.view-model';
import {RequestStepStatusEnum} from '@dto/requests/request-step-status.enum';
import {RequestStepGbgDto} from '@dto/requests/request-step-gbg.dto';
import {RequestStepGbgViewModel} from '../view-models/request-step-gbg.view-model';
import {RequestStepFileDownloadDto} from '@dto/requests/request-step-file-download.dto';
import {RequestStepFileDownloadViewModel} from '../view-models/request-step-file-download-view.model';
import {RequestStepFileUploadDto} from '@dto/requests/request-step-file-upload.dto';
import {RequestStepFileUploadViewModel} from '../view-models/request-step-file-upload-view.model';
import {FileConverterService} from '@shared/services/file-converter.service';
import {RequestListViewModel} from '../view-models/request-list.view-model';
import {InitiatedRequestDto} from '@dto/requests/initiated-request.dto';

@Injectable({
  providedIn: 'root'
})
export class RequestConverterService {
  private readonly fileConverterService = inject(FileConverterService);

  public convertRequestDtoToRequestViewModel(request: RequestDto): RequestViewModel {
    const readonly = (request.status === RequestStatusEnum.Cancelled || request.status === RequestStatusEnum.Completed || request.status === RequestStatusEnum.InReview);

    return {
      id: request.id,
      status: request.status,
      readonly: readonly,
      description: request.description,
      percentageComplete: request.percentageComplete,
      originalTemplateId: request.originalTemplateId ?? ''
    };
  }

  public convertInitiatedRequestDtoToRequestViewModel(dto: InitiatedRequestDto): RequestViewModel {
    const status = this.mapBcInitiatedStatus(dto.requestStatus);
    const readonly = (status === RequestStatusEnum.Cancelled || status === RequestStatusEnum.Completed || status === RequestStatusEnum.InReview);
    return {
      id: dto.requestId,
      status,
      readonly,
      description: dto.requestTypeDescription,
      percentageComplete: dto.requestPercentageComplete,
      originalTemplateId: dto.requestTypeId
    };
  }

  /**
   * Maps a BC status string from the initiate response to a portal RequestStatusEnum.
   *
   * Defense-in-depth: handles both raw BC PascalCase strings (e.g. 'InProgress') and
   * already-normalized portal kebab-case strings (e.g. 'in-progress'). The backend
   * normalizes via MapRequestStatusString before returning, so in practice only the
   * portal strings arrive — but we retain both cases as a safety net against BC API
   * format changes without a backend deploy.
   */
  private mapBcInitiatedStatus(bcStatus: string): RequestStatusEnum {
    switch (bcStatus) {
      case 'New':
      case RequestStatusEnum.New: return RequestStatusEnum.New;
      case 'InProgress':
      case RequestStatusEnum.InProgress: return RequestStatusEnum.InProgress;
      case 'InReview':
      case RequestStatusEnum.InReview: return RequestStatusEnum.InReview;
      case 'Completed':
      case RequestStatusEnum.Completed: return RequestStatusEnum.Completed;
      case 'Cancelled':
      case RequestStatusEnum.Cancelled: return RequestStatusEnum.Cancelled;
      default: return RequestStatusEnum.New;
    }
  }

  public convertRequestListDtoToRequestListViewModels(requestListDto: RequestListDto): RequestListViewModel[] {
    return requestListDto.requests.map(r => this.convertRequestDtoToRequestListViewModel(r));
  }

  private convertRequestDtoToRequestListViewModel(request: RequestDto): RequestListViewModel {
    const viewModel = this.convertRequestDtoToRequestViewModel(request);
    const detailsRouterLinkCommands = APP_REQUEST.toRouterLinkCommands(request.id);
    const label = this.getRequestButtonLabel(request.status);

    return {
      ...viewModel,
      detailsLabel: label,
      detailsRouterLinkCommands: detailsRouterLinkCommands
    };
  }

  private getRequestButtonLabel(requestStatus: RequestStatusEnum): string {
    switch (requestStatus) {
      case RequestStatusEnum.New:
      case RequestStatusEnum.InProgress:
        return 'Resume';
      case RequestStatusEnum.InReview:
      case RequestStatusEnum.Completed:
      case RequestStatusEnum.Cancelled:
        return 'View';
    }
    Validator.verifySwitchHandledAllOptions(requestStatus);
  }

  public convertRequestDetailDtoToRequestDetailViewModel(summary: RequestViewModel, requestDetail: RequestDetailDto): RequestDetailViewModel {
    const requestStepViewModels = requestDetail.steps.map(step => this.convertRequestStepDetailDtoToRequestStepDetailViewModel(step));
    return {
      ...summary,
      closeRouterLinkCommands: this.getCloseRouterLinkCommands(summary.status),
      steps: requestStepViewModels
    };
  }

  public convertRequestDtoAndStepsToRequestDetailViewModel(request: RequestDto, stepViewModels: RequestStepDetailViewModel[]): RequestDetailViewModel {
    const requestViewModel = this.convertRequestDtoToRequestViewModel(request);

    return {
      ...requestViewModel,
      closeRouterLinkCommands: this.getCloseRouterLinkCommands(request.status),
      steps: stepViewModels
    };
  }

  public convertRequestStepDetailDtoToRequestStepDetailViewModel(step: RequestStepDetailDto): RequestStepDetailViewModel {
    switch (step.type) {
      case RequestStepTypeEnum.AdaptiveCard:
        return this.convertRequestStepAdaptiveCardViewModel(step);
      case RequestStepTypeEnum.GbgIdScan:
        return this.convertRequestStepGbgViewModel(step);
      case RequestStepTypeEnum.FileDownload:
        return this.convertRequestStepFileDownloadViewModel(step);
      case RequestStepTypeEnum.FileUpload:
        return this.convertRequestStepFileUploadViewModel(step);
    }
    Validator.verifySwitchHandledAllOptions(step);
  }

  private convertRequestStepAdaptiveCardViewModel(step: RequestStepAdaptiveCardDto): RequestStepAdaptiveCardViewModel {
    return {
      id: step.id,
      type: RequestStepTypeEnum.AdaptiveCard,
      title: step.title,
      description: step.description,
      completed: step.status === RequestStepStatusEnum.Completed,
      template: step.template,
      data: step.data
    };
  }

  private convertRequestStepGbgViewModel(step: RequestStepGbgDto): RequestStepGbgViewModel {
    return {
      id: step.id,
      type: RequestStepTypeEnum.GbgIdScan,
      title: step.title,
      description: step.description,
      completed: step.status === RequestStepStatusEnum.Completed
    };
  }

  private convertRequestStepFileDownloadViewModel(step: RequestStepFileDownloadDto): RequestStepFileDownloadViewModel {
    const fileViewModels = this.fileConverterService.convertFileDtosToFileViewModels(step.files);

    return {
      id: step.id,
      type: RequestStepTypeEnum.FileDownload,
      title: step.title,
      description: step.description,
      completed: step.status === RequestStepStatusEnum.Completed,
      files: fileViewModels
    };
  }

  private convertRequestStepFileUploadViewModel(step: RequestStepFileUploadDto): RequestStepFileUploadViewModel {
    const fileViewModels = this.fileConverterService.convertFileDtosToFileViewModels(step.files);

    return {
      id: step.id,
      type: RequestStepTypeEnum.FileUpload,
      title: step.title,
      description: step.description,
      completed: step.status === RequestStepStatusEnum.Completed,
      files: fileViewModels
    };
  }

  private getCloseRouterLinkCommands(requestStatus: RequestStatusEnum): string[] {
    switch (requestStatus) {
      case RequestStatusEnum.New:
      case RequestStatusEnum.InProgress:
      case RequestStatusEnum.Cancelled:
        return APP_REQUESTS_IN_PROGRESS.toRouterLinkCommands();
      case RequestStatusEnum.InReview:
        return APP_REQUESTS_IN_REVIEW.toRouterLinkCommands();
      case RequestStatusEnum.Completed:
        return APP_REQUESTS_COMPLETED.toRouterLinkCommands();
    }
    Validator.verifySwitchHandledAllOptions(requestStatus);
  }
}
